/**
 * Carriers on the map: where each boat launches its drones, how many are still
 * aboard, and the leg to the next launch point.
 *
 * Drawn from the carrier plans the server pushes and the boat's own reported
 * position, so a ring turns from "launch here" to "launched" the moment the
 * executor fires it, whichever screen the operator happens to be on.
 *
 * Positions arrive at 10 Hz; the overlay is rebuilt at most twice a second.
 * The boat moves a few metres in that time, and a GeoJSON rebuild per fix is
 * the cost the drone markers were written to avoid.
 */

import { useEffect } from 'react';

import type { CarrierPlan } from '@/api/carriers';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { circleRing } from '@/map/useZoneLayer';
import { phaseOf, useCarrierStore, watchCarriers } from '@/stores/carrierStore';
import { useDroneStore } from '@/stores/droneStore';

export const CARRIER_SOURCE_ID = 'carrier-ops';
const RING_FILL_LAYER = 'carrier-ops-ring-fill';
const RING_LINE_LAYER = 'carrier-ops-ring-line';
const LEG_LAYER = 'carrier-ops-leg';
const LABEL_LAYER = 'carrier-ops-label';

/** Mirrors ARRIVAL_RADIUS_M in api/app/domain/carrier.py: the ring is the
 *  area the boat has to enter for the launch to fire. */
export const ARRIVAL_RADIUS_M = 25;

const REDRAW_MS = 500;

const PENDING = '#f2b84b';
const FIRED = '#7a7f87';

export type BoatPosition = { lat: number; lon: number } | null;

interface Feature {
  type: 'Feature';
  properties: Record<string, string | number | boolean>;
  geometry:
    | { type: 'Polygon'; coordinates: Array<Array<[number, number]>> }
    | { type: 'LineString'; coordinates: Array<[number, number]> }
    | { type: 'Point'; coordinates: [number, number] };
}

/** The overlay for every plan, as GeoJSON features. Pure, so it is tested alone. */
export function carrierFeatures(
  plans: readonly CarrierPlan[],
  positionOf: (droneId: string) => BoatPosition,
): Feature[] {
  const features: Feature[] = [];
  for (const plan of plans) {
    const fired = new Set(plan.fired);
    const points = new Map<number, { lat: number; lon: number; aboard: number; total: number }>();
    for (const entry of plan.embarked) {
      const point = points.get(entry.deployIndex) ?? {
        lat: entry.deployLat,
        lon: entry.deployLon,
        aboard: 0,
        total: 0,
      };
      point.total += 1;
      if (phaseOf(plan, entry.droneId) === 'aboard') point.aboard += 1;
      points.set(entry.deployIndex, point);
    }

    let next: { lat: number; lon: number } | null = null;
    for (const [index, point] of [...points.entries()].sort((a, b) => a[0] - b[0])) {
      const pending = !fired.has(index) && point.aboard > 0;
      if (pending && next === null) next = point;
      const color = pending ? PENDING : FIRED;
      features.push({
        type: 'Feature',
        properties: { kind: 'ring', color, carrierId: plan.carrierId },
        geometry: { type: 'Polygon', coordinates: [circleRing(point.lat, point.lon, ARRIVAL_RADIUS_M)] },
      });
      features.push({
        type: 'Feature',
        properties: {
          kind: 'label',
          color,
          label: pending ? `LAUNCH ${point.aboard} · WP ${index + 1}` : `LAUNCHED · WP ${index + 1}`,
        },
        geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
      });
    }

    const boat = positionOf(plan.carrierId);
    if (!boat) continue;
    const aboard = plan.embarked.filter((e) => phaseOf(plan, e.droneId) === 'aboard').length;
    features.push({
      type: 'Feature',
      properties: { kind: 'label', color: PENDING, label: `${aboard} ABOARD` },
      geometry: { type: 'Point', coordinates: [boat.lon, boat.lat] },
    });
    if (next) {
      features.push({
        type: 'Feature',
        properties: { kind: 'leg', color: PENDING },
        geometry: {
          type: 'LineString',
          coordinates: [
            [boat.lon, boat.lat],
            [next.lon, next.lat],
          ],
        },
      });
    }
  }
  return features;
}

function boatPosition(droneId: string): BoatPosition {
  const drone = useDroneStore.getState().drones.get(droneId);
  const lat = drone?.scalars.lat;
  const lon = drone?.scalars.lon;
  if (typeof lat !== 'number' || typeof lon !== 'number' || (lat === 0 && lon === 0)) return null;
  return { lat, lon };
}

export function createCarrierLayer(map: MapLike): { destroy: () => void } {
  if (!map.getSource(CARRIER_SOURCE_ID)) {
    map.addSource(CARRIER_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  const layers: Array<Record<string, unknown>> = [
    {
      id: RING_FILL_LAYER,
      type: 'fill',
      source: CARRIER_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'ring'],
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.14 },
    },
    {
      id: RING_LINE_LAYER,
      type: 'line',
      source: CARRIER_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'ring'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 1.5 },
    },
    {
      id: LEG_LAYER,
      type: 'line',
      source: CARRIER_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'leg'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 1.5, 'line-dasharray': [2, 2] },
    },
    {
      id: LABEL_LAYER,
      type: 'symbol',
      source: CARRIER_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'label'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-offset': [0, 1.6],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    },
  ];
  for (const layer of layers) {
    if (!map.getLayer(String(layer.id))) map.addLayer(layer as never);
  }

  let timer: number | null = null;
  const redraw = () => {
    timer = null;
    const source = geoJsonSource(map, CARRIER_SOURCE_ID);
    if (!source) return;
    const plans = Object.values(useCarrierStore.getState().plans);
    source.setData({ type: 'FeatureCollection', features: carrierFeatures(plans, boatPosition) });
  };
  const schedule = () => {
    if (timer === null) timer = window.setTimeout(redraw, REDRAW_MS);
  };
  redraw();
  const offPlans = useCarrierStore.subscribe(redraw);
  const offDrones = useDroneStore.subscribe(() => {
    if (Object.keys(useCarrierStore.getState().plans).length) schedule();
  });

  return {
    destroy: () => {
      offPlans();
      offDrones();
      if (timer !== null) window.clearTimeout(timer);
      for (const id of [LABEL_LAYER, LEG_LAYER, RING_LINE_LAYER, RING_FILL_LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(CARRIER_SOURCE_ID)) map.removeSource(CARRIER_SOURCE_ID);
    },
  };
}

export function useCarrierLayer(): void {
  const controller = useMapController();
  // The layer feeds itself. A layer that draws data another screen was meant
  // to load draws nothing when Command is opened first.
  useEffect(() => {
    watchCarriers();
    void useCarrierStore.getState().load();
  }, []);
  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createCarrierLayer(map);
        return () => layer.destroy();
      }),
    [controller],
  );
}
